import { useEffect, useMemo, useState } from 'react';

import { equalTo, onValue, orderByChild, query, ref } from 'firebase/database';
import type { Query as RTDBQuery } from 'firebase/database';

import { database } from '@src/firebase/firebase';

import type { LiveStatusByUser, UserLiveStatus } from '../types';
import { useOrgAccess } from './useOrgAccess';

interface RawRTDBUser {
  device?: { DEVICE_INFO?: UserLiveStatus['device'] };
  positions?: { UNIQUE_POSITION?: UserLiveStatus['position'] };
  trackingState?: UserLiveStatus['trackingState'];
}

interface LiveStatusState {
  statusByQuery: Record<number, LiveStatusByUser>;
  /** Queries (by index) that delivered their first snapshot or errored. */
  responded: Record<number, true>;
  queryCount: number;
  error: Error | null;
}

interface UseUsersLiveStatusReturn {
  statusByUser: LiveStatusByUser;
  loading: boolean;
  error: Error | null;
}

const parseSnapshotValue = (value: Record<string, RawRTDBUser> | null): LiveStatusByUser => {
  const parsed: LiveStatusByUser = {};
  Object.entries(value ?? {}).forEach(([uid, raw]) => {
    parsed[uid] = {
      device: raw?.device?.DEVICE_INFO,
      position: raw?.positions?.UNIQUE_POSITION,
      trackingState: raw?.trackingState,
    };
  });
  return parsed;
};

/**
 * Live device + position status from RTDB, normalized to a map keyed by uid.
 *
 * Org-wide roles use a single `users` listener filtered by organization key;
 * division-scoped users get one listener per accessible division. Division
 * visibility filtering is NOT applied here — rows are assembled by joining with
 * the (already access-filtered) roster, so extra entries are simply unused.
 */
export const useUsersLiveStatus = (orgId: string): UseUsersLiveStatusReturn => {
  const { isAdmin, accessibleDivisionIds, accessKey } = useOrgAccess(orgId);
  const [state, setState] = useState<LiveStatusState>({
    statusByQuery: {},
    responded: {},
    queryCount: 1,
    error: null,
  });

  useEffect(() => {
    if (!orgId) {
      setState({ statusByQuery: {}, responded: {}, queryCount: 0, error: null });
      return undefined;
    }

    const queries: RTDBQuery[] = isAdmin
      ? [query(ref(database, 'users'), orderByChild(`organizations/${orgId}`), equalTo(orgId))]
      : accessibleDivisionIds.map(divId =>
          query(ref(database, 'users'), orderByChild(`divisions/${divId}`), equalTo(divId)),
        );

    if (queries.length === 0) {
      setState({ statusByQuery: {}, responded: {}, queryCount: 0, error: null });
      return undefined;
    }

    setState({ statusByQuery: {}, responded: {}, queryCount: queries.length, error: null });

    // First-response tracking lives INSIDE the state (idempotent updates):
    // external mutable flags race with React's deferred updater execution and
    // can leave the loading flag stuck forever.
    const unsubscribes = queries.map((q, index) =>
      onValue(
        q,
        snapshot => {
          const parsed = parseSnapshotValue(snapshot.val());
          setState(prev => ({
            ...prev,
            statusByQuery: { ...prev.statusByQuery, [index]: parsed },
            responded: { ...prev.responded, [index]: true },
          }));
        },
        error => {
          setState(prev => ({
            ...prev,
            responded: { ...prev.responded, [index]: true },
            error,
          }));
        },
      ),
    );

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
    // accessKey already encodes orgId, isAdmin and the accessible division list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessKey]);

  const statusByUser = useMemo(() => {
    const merged: LiveStatusByUser = {};
    Object.values(state.statusByQuery).forEach(queryStatus => {
      Object.assign(merged, queryStatus);
    });
    return merged;
  }, [state.statusByQuery]);

  const loading = Object.keys(state.responded).length < state.queryCount;

  return { statusByUser, loading, error: state.error };
};
